<script setup lang="ts">
/**
 * HLOS-138 — ConversationInit (boot/setup).
 *
 * Carrega Pyodide + injeta master key no bridge + instala `webllm_runtime`
 * (HLOS-90). Após sucesso, transiciona phase para `'chat'`. Status (loading/
 * error) via `ConversationStatus` compartilhado (HLOS-140).
 *
 * **Constitucional:** CL-07 (zero fetch externo no load) · CL-09
 * (aria-live no loading) · Princípio V (error com saída) · CC-05 · XI.
 *
 * **Ref:** HLOS-140, HLOS-138, HLOS-15, HLOS-48, HLOS-90.
 */
import { onMounted } from "vue";
import { useConversationPhase } from "../composables/useConversationPhase";
import { getVaultAPI } from "../../vault/adapter";
import {
  initPyodide,
  passMasterKeyToKernel,
  shouldUseMock,
} from "../../pyodide/bridge";
import ConversationStatus from "./ConversationStatus.vue";

const emit = defineEmits<{
  (e: "end"): void;
}>();

const phase = useConversationPhase();

onMounted(async () => {
  // Mock mode: sem Pyodide real, sem master key. Chat funciona via mock.
  if (shouldUseMock()) {
    phase.markReady();
    return;
  }

  // HLOS-48: injeta master key (DEK do vault TS-side) no bridge Pyodide.
  // Usa VaultAPI (HLOS-134 boundary) — store não expõe `vault` diretamente.
  const vault = await getVaultAPI();
  if (!vault.isUnlocked()) {
    console.warn(
      "[ConversationInit] Vault não disponível; master key NÃO será injetada.",
    );
    phase.markError("vault_unavailable");
    return;
  }

  try {
    // HLOS-134: VaultAPI expõe getDekBytes(); convertemos para hex (bridge).
    const dekBytes = vault.getDekBytes();
    const masterKeyHex = Array.from(dekBytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    passMasterKeyToKernel(masterKeyHex);
    console.info(
      "[ConversationInit] master key registrada no bridge (64 chars hex).",
    );
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    console.warn("[ConversationInit] Falha ao extrair master key:", msg);
    phase.markError(msg);
    return;
  }

  // HLOS-194: initPyodide agora centraliza installWebLLMRuntime(raw)
  // internamente (bridge.ts). Single source of truth — qualquer call
  // site (boot aqui, ou runtime via useProcessTurn) recebe o runtime
  // instalado. Não precisamos mais instalar aqui. ConversationInit
  // continua responsável por master key (HLOS-48).
  try {
    await initPyodide();
    console.info("[ConversationInit] Pyodide pronto (incl. webllm_runtime).");
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    console.warn(
      "[ConversationInit] initPyodide falhou (não-bloqueante):",
      msg,
    );
  }

  // Sucesso: Chat é renderizado pelo wrapper.
  phase.markReady();
});
</script>

<template>
  <!--
    HLOS-140: status compartilhado com Chat (ConversationStatus).
    variant='boot' distingue o cold-start do Pyodide (boot-loading, HLOS-15)
    do loading de turno (kernel-loading, no Chat).
  -->
  <ConversationStatus
    :loading="phase.phase.value === 'init'"
    :errored="phase.phase.value === 'error'"
    variant="boot"
    @end="emit('end')"
  />
</template>
